import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import TableWithState from "../../components/common/TableWithState";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import DestructiveConfirm from "../../components/common/DestructiveConfirm";
import { getRequirement } from "../../api/requirements";
import { getMappedControls, bulkAddControls, removeControlMapping } from "../../api/mappings";
import { getControls } from "../../api/controls";
import { useToast } from "../../components/common/ToastProvider";
import Button from "../../ui/Button";

export default function ControlRequirementMapping() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const [mappedLimit] = useState(20);
  const [mappedOffset, setMappedOffset] = useState(0);
  const [mappedSort, setMappedSort] = useState("code:asc");

  const [controlLimit] = useState(20);
  const [controlOffset, setControlOffset] = useState(0);
  const [controlSort, setControlSort] = useState("code:asc");
  const [controlSearch, setControlSearch] = useState("");
  const [controlCategory, setControlCategory] = useState("");

  const [selectedControls, setSelectedControls] = useState(new Set());
  const [confirmRemove, setConfirmRemove] = useState(null);

  const { data: requirement, isLoading: reqLoading } = useQuery({
    queryKey: ["requirement", rid],
    queryFn: () => getRequirement(rid),
    enabled: Boolean(rid),
  });

  const mappedQuery = useQuery({
    queryKey: ["requirementControls", rid, { mappedLimit, mappedOffset, mappedSort }],
    queryFn: () =>
      getMappedControls(rid, { limit: mappedLimit, offset: mappedOffset, sort: mappedSort }),
    enabled: Boolean(rid),
    keepPreviousData: true,
  });

  const controlsQuery = useQuery({
    queryKey: ["controls", { limit: controlLimit, offset: controlOffset, q: controlSearch, category: controlCategory, sort: controlSort }],
    queryFn: () =>
      getControls({
        limit: controlLimit,
        offset: controlOffset,
        q: controlSearch,
        category: controlCategory,
        sort: controlSort,
      }),
    keepPreviousData: true,
  });

  const addMutation = useMutation({
    mutationFn: (controlIds) => bulkAddControls(rid, controlIds),
    onSuccess: (res) => {
      success(`Added ${res.added} control(s), skipped ${res.skipped}`);
      setSelectedControls(new Set());
      queryClient.invalidateQueries({ queryKey: ["requirementControls", rid] });
      queryClient.invalidateQueries({ queryKey: ["controls"] });
    },
    onError: (err) => showError(err?.detail || "Failed to add controls"),
  });

  const removeMutation = useMutation({
    mutationFn: ({ requirementId, controlId }) => removeControlMapping(requirementId, controlId),
    onSuccess: () => {
      success("Mapping removed");
      queryClient.invalidateQueries({ queryKey: ["requirementControls", rid] });
    },
    onError: (err) => showError(err?.detail || "Failed to remove mapping"),
  });

  const mappedColumns = useMemo(
    () => [
      { label: "Code", accessor: "code", sortable: true },
      { label: "Title", accessor: "title", sortable: true },
      { label: "Category", accessor: "category", sortable: true },
    ],
    []
  );

  const controlColumns = useMemo(
    () => [
      { label: "", accessor: "select", sortable: false },
      { label: "Code", accessor: "code", sortable: true },
      { label: "Title", accessor: "title", sortable: true },
      { label: "Category", accessor: "category", sortable: true },
    ],
    []
  );

  const breadcrumbItems = [
    { label: "Frameworks", to: "/frameworks" },
    requirement?.framework_id
      ? { label: "Framework", to: `/frameworks/${requirement.framework_id}` }
      : null,
    requirement ? { label: requirement.code, to: `/requirements/${requirement.id}` } : null,
    { label: "Map Controls" },
  ].filter(Boolean);

  const toggleSelect = (id) => {
    setSelectedControls((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAddSelected = () => {
    if (selectedControls.size === 0) return;
    addMutation.mutate(Array.from(selectedControls));
  };

  return (
    <div>
      <Breadcrumbs items={breadcrumbItems} />
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
        <h1>Map Controls</h1>
        <Button type="button" onClick={() => navigate(`/requirements/${rid}`)} variant="secondary">
          Back to Requirement
        </Button>
      </div>
      {reqLoading && <Loader />}
      {requirement && (
        <div style={{ marginBottom: "16px" }}>
          <div>
            <strong>{requirement.code}</strong> - {requirement.title}
          </div>
          <div>{requirement.description}</div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <div>
          <h3>Mapped Controls</h3>
          {mappedQuery.isLoading && <Loader />}
          {mappedQuery.isError && (
            <div>Error: {mappedQuery.error?.detail || "Failed to load mappings"}</div>
          )}
          {mappedQuery.data && (
            <TableWithState
              columns={mappedColumns}
              data={mappedQuery.data.items}
              total={mappedQuery.data.total}
              limit={mappedQuery.data.limit}
              offset={mappedQuery.data.offset}
              sort={mappedSort}
              onSort={setMappedSort}
              onPageChange={setMappedOffset}
              renderActions={(row) => (
                <Button
                  type="button"
                  data-testid={`remove-mapping-${row.id}`}
                  onClick={() => setConfirmRemove(row)}
                  variant="destructive"
                >
                  Remove
                </Button>
              )}
            />
          )}
          {!mappedQuery.isLoading && mappedQuery.data?.items?.length === 0 && (
            <EmptyState title="No mapped controls" />
          )}
        </div>

        <div>
          <h3>Available Controls</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setControlOffset(0);
            }}
            style={{ display: "flex", gap: "8px", marginBottom: "12px" }}
          >
            <input
              value={controlSearch}
              onChange={(e) => setControlSearch(e.target.value)}
              placeholder="Search controls"
              data-testid="control-picker-search"
            />
            <input
              value={controlCategory}
              onChange={(e) => setControlCategory(e.target.value)}
              placeholder="Category"
              data-testid="control-picker-category"
            />
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
          {controlsQuery.isLoading && <Loader />}
          {controlsQuery.isError && (
            <div>Error: {controlsQuery.error?.detail || "Failed to load controls"}</div>
          )}
          {controlsQuery.data && (
            <TableWithState
              columns={controlColumns}
              data={controlsQuery.data.items.map((c) => ({
                ...c,
                select: (
                  <input
                    type="checkbox"
                    checked={selectedControls.has(c.id)}
                    onChange={() => toggleSelect(c.id)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`control-select-${c.id}`}
                  />
                ),
              }))}
              total={controlsQuery.data.total}
              limit={controlsQuery.data.limit}
              offset={controlsQuery.data.offset}
              sort={controlSort}
              onSort={setControlSort}
              onPageChange={setControlOffset}
            />
          )}
          {!controlsQuery.isLoading && controlsQuery.data?.items?.length === 0 && (
            <EmptyState title="No controls available" />
          )}
          <Button
            type="button"
            data-testid="add-selected-controls"
            onClick={handleAddSelected}
            disabled={selectedControls.size === 0}
            variant="primary"
          >
            Add selected controls
          </Button>
        </div>
      </div>

      <DestructiveConfirm
        actionId="CONTROL_CATALOG_MAPPING_DELETE"
        open={Boolean(confirmRemove)}
        resourceDisplayName={confirmRemove?.title || confirmRemove?.code}
        onCancel={() => setConfirmRemove(null)}
        onConfirm={() => {
          if (confirmRemove) {
            removeMutation.mutate({ requirementId: rid, controlId: confirmRemove.id });
            setConfirmRemove(null);
          }
        }}
      />
    </div>
  );
}
